import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Phone, Mail } from "lucide-react";
import { useThemeContext } from "../../contexts/ThemeContext";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isDark, toggleTheme } = useThemeContext();
  const location = useLocation();

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "Gallery", path: "/gallery" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact Us", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const EMAIL =
    process.env.REACT_APP_EMAIL || "brajdecoranddesigner@gmail.com";
  const PHONE =
    process.env.REACT_APP_PHONE_NUMBER || "+91 7311118002";

  return (
    <header className="fixed top-0 w-full z-50">
      {/* 🔹 Top Bar – Gold & Black Gradient */}
      <div className="bg-gradient-to-r from-black via-yellow-600 to-black text-white text-xs sm:text-sm font-semibold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-col sm:flex-row gap-2 sm:gap-0 justify-between items-center">
          <div className="flex flex-wrap items-center gap-4">
            <a
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-2 hover:text-yellow-300 transition"
            >
              <Mail className="w-4 h-4" />
              <span className="break-all sm:break-normal">
                brajdecoranddesigner@gmail.com
              </span>
            </a>
            <a
              href={`tel:${PHONE}`}
              className="flex items-center gap-2 hover:text-yellow-300 transition"
            >
              <Phone className="w-4 h-4" />
              <span>+91 7311118002</span>
            </a>
          </div>

          <p className="hidden md:block text-yellow-300 font-bold tracking-wide text-center">
            Braj Decor And Designers — Professional Architecture & Design Services
          </p>
        </div>
      </div>

      {/* 🔹 Main Navigation */}
      <nav className="bg-gradient-to-r from-black via-gray-900 to-black shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Braj Decor And Designers"
              className="w-[80px] sm:w-[90px] h-[60px] sm:h-[70px] object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8 mx-auto">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm font-semibold tracking-wide transition ${
                  isActive(item.path)
                    ? "text-yellow-400 border-b-2 border-yellow-400 pb-1"
                    : "text-yellow-300 hover:text-yellow-400"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-400 text-black hover:from-yellow-400 hover:to-yellow-300 transition"
            >
              {isDark ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            <Link
              to="/contact"
              className="px-5 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold hover:opacity-90 transition"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-yellow-400"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* 🔹 Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-gradient-to-b from-black to-gray-900 border-t border-yellow-500"
            >
              <div className="px-6 py-5 space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block text-sm font-semibold ${
                      isActive(item.path)
                        ? "text-yellow-400"
                        : "text-yellow-300 hover:text-yellow-400"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}

                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold"
                >
                  Get Quote
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;
